// Global Instructions Rule Applied!

import { useCallback } from 'react'
import { AccountRecordFields } from '../../../state_manager/data_models/account_record.ts'
import { AccountRecord, validateAccountRecord } from '../../../state_manager/data_models/account_record.ts'
import { AccountRoleEntryFields } from '../../../state_manager/data_models/account_role_entry.ts'
import { FIELD_KEY_LABEL } from '../../../state_manager/globals.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../state_manager/constants/lookupCategories.ts'

/**
 * Custom hook for managing company form validation and data transformation
 * Handles form validation and converts display values to database values
 */
export const useCompaniesForm = (
  getLookupIdByLabel: (label: string, category: string) => string | null,
  getLookupLabel: (id: string) => string,
  getLookupOptions?: (category: string) => Array<{ value: string; label: string }>
) => {
  // Helper function to get category name for lookup fields
  const getFieldCategory = useCallback((fieldName: string) => {
    const categoryMapping: Record<string, string> = {
      industry: LOOKUP_CATEGORY_INDUSTRY,
      roles: 'Account Role'
    }
    return categoryMapping[fieldName] || ''
  }, [])

  // Convert display values (lookup labels) to database values (UUIDs)
  const convertDisplayToDbValues = useCallback(
    (displayData: Record<string, any>): Record<string, any> => {
      const dbData = { ...displayData }

      // Define field mappings and their conversion rules
      const fieldMappings = [
        // Industry lookup field
        {
          displayField: AccountRecordFields.accounts__industry,
          dbField: AccountRecordFields.accounts__industry,
          category: LOOKUP_CATEGORY_INDUSTRY,
          convert: (value: any) => {
            if (!value || (typeof value === 'string' && value.trim() === '')) return null
            if (typeof value === 'string') {
              // Check if value is already a UUID by checking if it exists in lookup options
              if (getLookupOptions) {
                const options = getLookupOptions(LOOKUP_CATEGORY_INDUSTRY)
                const existsAsValue = options.some((opt) => opt.value === value)
                if (existsAsValue) {
                  // Value is already a UUID, return it as-is
                  return value
                }
              }
              // Value is not a UUID, try to convert from label to UUID
              return getLookupIdByLabel(value, LOOKUP_CATEGORY_INDUSTRY) || null
            }
            return value // Already a UUID (non-string)
          }
        }
      ]

      // Apply conversions
      fieldMappings.forEach(({ displayField, dbField, convert }) => {
        if (dbData[displayField] !== undefined) {
          const convertedValue = convert(dbData[displayField])

          if (convertedValue === undefined) {
            // Exclude this field from the update
            delete dbData[displayField]
          } else if (dbField !== displayField) {
            // Map to different field name
            dbData[dbField] = convertedValue
            delete dbData[displayField]
          } else {
            // Same field name, just convert value
            dbData[displayField] = convertedValue
          }
        }
      })

      // Clean up: convert empty strings to null for nullable fields
      const nullableFields = [
        AccountRecordFields.accounts__legal_name,
        AccountRecordFields.accounts__industry,
        AccountRecordFields.accounts__website,
        AccountRecordFields.accounts__description,
        AccountRecordFields.accounts__business_city_code,
        AccountRecordFields.accounts__business_country_code
      ]

      nullableFields.forEach((field) => {
        if (dbData[field] === '' || (typeof dbData[field] === 'string' && dbData[field].trim() === '')) {
          dbData[field] = null
        }
      })

      return dbData
    },
    [getLookupIdByLabel, getLookupOptions]
  )

  // Validate required fields
  const validateRequiredFields = useCallback(
    (formData: Record<string, any>): { isValid: boolean; errors: Record<string, string> } => {
      const errors: Record<string, string> = {}

      // Required fields
      if (!formData[AccountRecordFields.accounts__name] || formData[AccountRecordFields.accounts__name].trim() === '') {
        errors[AccountRecordFields.accounts__name] = 'Company name is required'
      }

      // Validate role field (prog__account_roles) - must have at least one role selected
      const roles = formData.prog__account_roles || formData.role || []
      if (!Array.isArray(roles) || roles.length === 0) {
        errors.prog__account_roles = 'Role is required'
      }

      return {
        isValid: Object.keys(errors).length === 0,
        errors
      }
    },
    []
  )

  // Transform form data to AccountRecord format
  const transformFormDataToAccountRecord = useCallback(
    (formData: Record<string, any>): AccountRecord => {
      // Convert display values to database values
      const dbData = convertDisplayToDbValues(formData)

      // Generate UUID for accounts__id if not present (required for new companies)
      if (!dbData[AccountRecordFields.accounts__id]) {
        dbData[AccountRecordFields.accounts__id] = crypto.randomUUID()
      }

      // Extract string labels from city/country objects if they're objects
      const extractLabel = (value: any): string | null => {
        if (!value) return null
        if (typeof value === 'string') return value
        if (typeof value === 'object') {
          return value.label || value.name || null
        }
        return null
      }

      // Extract city code from city object
      const extractCityCode = (value: any): number | null => {
        if (!value) return null
        if (typeof value === 'object' && value !== null) {
          return value.value ?? null
        }
        return null
      }

      // Handle business city/country labels
      if (dbData[AccountRecordFields.accounts__business_city_label] !== undefined) {
        const cityObject = dbData[AccountRecordFields.accounts__business_city_label]
        // Extract city code from city object before extracting label
        const cityCode = extractCityCode(cityObject)
        if (cityCode !== null) {
          dbData[AccountRecordFields.accounts__business_city_code] = cityCode
        }
        // Extract and set city label
        const cityLabel = extractLabel(cityObject)
        dbData[AccountRecordFields.accounts__business_city_label] = cityLabel
      }

      if (dbData[AccountRecordFields.accounts__business_country_label] !== undefined) {
        const countryLabel = extractLabel(dbData[AccountRecordFields.accounts__business_country_label])
        dbData[AccountRecordFields.accounts__business_country_label] = countryLabel
      }

      // Handle office city/country labels - these are Redux-only fields, remove them before server submission
      // The server only needs office_city_code and office_country_code, not the label fields
      if (dbData['accounts__office_city_label'] !== undefined) {
        delete dbData['accounts__office_city_label']
      }

      if (dbData['accounts__office_country_label'] !== undefined) {
        delete dbData['accounts__office_country_label']
      }

      // Handle office city - extract code and label from city object
      // The backend needs the label to upsert the city to the cities table
      if (dbData['accounts__office_city_code__label'] !== undefined) {
        const cityObject = dbData['accounts__office_city_code__label']
        // Extract city code from city object
        const cityCode = extractCityCode(cityObject)
        if (cityCode !== null) {
          dbData[AccountRecordFields.accounts__office_city_code] = cityCode
        }
        // Extract city label string from city object and keep it for backend
        // Backend uses this to upsert the city to the cities table during account creation
        const cityLabel = extractLabel(cityObject)
        dbData['accounts__office_city_code__label'] = cityLabel
      }

      if (dbData['accounts__office_country_code__label'] !== undefined) {
        delete dbData['accounts__office_country_code__label']
      }

      // Remove incorrectly formatted business country label field if it exists
      if (dbData['accounts__business_country_code__label'] !== undefined) {
        delete dbData['accounts__business_country_code__label']
      }

      // Transform aliases from comma-separated string to array
      if (dbData[AccountRecordFields.accounts__aliases] !== undefined) {
        const aliasesValue = dbData[AccountRecordFields.accounts__aliases]
        if (typeof aliasesValue === 'string') {
          // Convert comma-separated string to array
          const aliasesArray = aliasesValue
            .split(',')
            .map((alias: string) => alias.trim())
            .filter((alias: string) => alias.length > 0)
          dbData[AccountRecordFields.accounts__aliases] = aliasesArray.length > 0 ? aliasesArray : null
        } else if (!Array.isArray(aliasesValue)) {
          // If it's not a string or array, set to null
          dbData[AccountRecordFields.accounts__aliases] = null
        }
        // If it's already an array, keep it as is
      }

      // Strip internal keys from city/country valueTransformers (used for patch field mapping, not API)
      const internalKeysToStrip = ['cityCode', 'cityName', 'state', 'countryCode']
      internalKeysToStrip.forEach((key) => {
        if (key in dbData) delete dbData[key]
      })

      // Transform account roles from array of strings to array of AccountRoleEntry objects
      if (dbData.prog__account_roles !== undefined && Array.isArray(dbData.prog__account_roles)) {
        const accountId = dbData[AccountRecordFields.accounts__id]
        dbData.prog__account_roles = dbData.prog__account_roles
          .filter((role: any) => role !== null && role !== undefined)
          .map((role: any) => {
            // If it's already an object with the right structure, ensure it has an action
            if (typeof role === 'object' && role[AccountRoleEntryFields.account_roles__account_role_choice_id]) {
              // If no action is specified and no ID exists, it's a new role - set action to 'insert'
              if (
                !role[AccountRoleEntryFields.prog__account_roles__action] &&
                !role[AccountRoleEntryFields.account_roles__id]
              ) {
                role[AccountRoleEntryFields.prog__account_roles__action] = 'insert'
              }
              return role
            }
            // If it's a string (role ID), convert to AccountRoleEntry object
            const roleId = typeof role === 'string' ? role : role?.value || role?.account_role_choice_id
            if (!roleId) return null

            return {
              [AccountRoleEntryFields.account_roles__id]: undefined,
              [AccountRoleEntryFields.account_roles__account_id]: accountId,
              [AccountRoleEntryFields.account_roles__account_role_choice_id]: roleId,
              [AccountRoleEntryFields.prog__account_roles__action]: 'insert'
            }
          })
          .filter((role: any) => role !== null)
      }

      // Validate the transformed data
      try {
        return validateAccountRecord(dbData) as AccountRecord
      } catch (error) {
        throw new Error(`Account record validation failed: ${error}`)
      }
    },
    [convertDisplayToDbValues]
  )

  return {
    convertDisplayToDbValues,
    validateRequiredFields,
    transformFormDataToAccountRecord,
    getFieldCategory
  }
}
